import { Link, Outlet, ScrollRestoration, useLocation } from "react-router";
import { Logo, StatsProvider, to } from "@/site/parts";
import { useTheme } from "@/site/theme";

const NAV = [
  { label: "Identify", href: to.home, prefixes: ["/identify/"] },
  { label: "Registry", href: to.registry, prefixes: ["/shark/"] },
  { label: "Method", href: to.about, prefixes: [] },
] as const;

function Nav() {
  const { pathname } = useLocation();
  return (
    <nav className="in-nav" aria-label="Site">
      {NAV.map(({ label, href, prefixes }) => {
        const exact = pathname === href;
        const within = exact || prefixes.some((p) => pathname.startsWith(p));
        return (
          <Link key={href} to={href} aria-current={exact ? "page" : undefined} data-on={within}>
            {label}
          </Link>
        );
      })}
    </nav>
  );
}

function ThemeToggle() {
  const [theme, setTheme] = useTheme();
  return (
    <div className="in-toggle" role="group" aria-label="Colour theme">
      {(["light", "dark"] as const).map((t) => (
        <button key={t} type="button" aria-pressed={theme === t} onClick={() => setTheme(t)}>
          {t}
        </button>
      ))}
    </div>
  );
}

function Header() {
  return (
    <header className="in-header">
      <div className="in-wrap in-header-row">
        <Link to={to.home} className="in-brand" aria-label="Gillspotter, home">
          <Logo />
          Gillspotter
          <span className="in-brand-sub">/ Photo-ID instrument</span>
        </Link>
        <Nav />
        <ThemeToggle />
      </div>
    </header>
  );
}

function Footer() {
  return (
    <footer className="in-footer">
      <div className="in-wrap in-grid" style={{ rowGap: 20 }}>
        <div>
          <p className="label">Gillspotter</p>
          <p className="muted" style={{ fontSize: 14, marginTop: 6 }}>
            Whale shark photo-identification. Spots read by SAM 3.1, matched as constellations.
          </p>
        </div>
        <div>
          <p className="label">Method</p>
          <p className="muted" style={{ fontSize: 14, marginTop: 6 }}>
            Groth (1986) triangle matching, after Arzoumanian, Holmberg &amp; Norman (2005), fused
            with MiewID.{" "}
            <Link to={to.about} className="link-u" style={{ color: "var(--ink)" }}>
              The full method
            </Link>
          </p>
        </div>
        <div>
          <p className="label">Photographs</p>
          <p className="muted" style={{ fontSize: 14, marginTop: 6 }}>
            Each stays its photographer&rsquo;s, shown with its credit and licence.{" "}
            <Link to={`${to.about}#sources`} className="link-u" style={{ color: "var(--ink)" }}>
              Sources
            </Link>
          </p>
        </div>
        <div>
          <p className="label">Registry</p>
          <p className="muted" style={{ fontSize: 14, marginTop: 6 }}>
            LILA (Wild Me), the whaleshark.org export, iNaturalist, GBIF and Wikimedia, unified.{" "}
            <Link to={to.registry} className="link-u" style={{ color: "var(--ink)" }}>
              Browse
            </Link>
          </p>
        </div>
      </div>
    </footer>
  );
}

/**
 * Header, page, footer. ScrollRestoration puts back/forward where you were, sends new pages to
 * the top and in-page anchors to their section.
 */
export function Shell() {
  return (
    <div className="site">
      <ScrollRestoration />
      <StatsProvider>
        <a href="#main" className="skip-link">
          Skip to content
        </a>
        <Header />
        <main className="in-main" id="main" tabIndex={-1}>
          <Outlet />
        </main>
        <Footer />
      </StatsProvider>
    </div>
  );
}
